import { Box } from "@mui/system";
import { Contact, ContactType, useGetAllMembersOfLazyQuery } from "@repo/server-api";
import { ContactExtended, OptionType, RuleFactoryType, TabKeys } from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import React, { useState, useMemo } from "react";

import { setContactValues, validErrorState } from "@repo/legacy/components/helper/Index";
import {
	useCreatePersonContact,
	useUpdatePersonContact,
} from "@repo/legacy/components/hooks/ContactMutationHooks";
import DialogActions from "@repo/legacy/components/shared/sections/DialogActions";

import DialogTabs from "./DialogWithTabs";
import Address from "../additionalInformation/Address";
import DepartmentInfo from "../additionalInformation/DepartmentInformation";
import Dropdown from "../sections/Dropdown";

export default function DialogExistingContactPerson(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: (contact: ContactExtended) => void;
		contact: ContactExtended | null;
		setContact: (value: ContactExtended | null) => void;
		ruleFactory: RuleFactoryType;
		companyId: string;
		saveClicked: boolean;
		setSaveClicked: (value: boolean) => void;
	}>,
) {
	const { t } = useTranslation();

	const [selectedContact, setSelectedContact] = useState<OptionType | null>(null);
	const contact = useMemo<ContactExtended>(() => {
		if (!props.contact) setSelectedContact(null);
		return {
			...setContactValues(props.contact),
			contactTypes: [
				...(props.contact?.contactTypes.filter(
					(type) => type !== ContactType.ContactPerson && type !== ContactType.Person,
				) ?? []),
				ContactType.Person,
				ContactType.ContactPerson,
			],
		};
	}, [props.contact, props.open]);
	const [createPersonContact] = useCreatePersonContact();
	const [updatePersonContact] = useUpdatePersonContact();

	const [tabIndex, setTabIndex] = useState<number>(0);

	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const [elements, setElements] = useState<Contact[]>([]);

	const [getMembers] = useGetAllMembersOfLazyQuery({
		fetchPolicy: "no-cache",
		variables: {
			first: 100,
			contactId: props.companyId,
		},
		onCompleted(data) {
			if (data && data.allMembersOf) {
				setElements(data.allMembersOf?.nodes as Contact[]);
			}
		},
	});

	const open = useMemo<boolean>(() => {
		setTabIndex(0);
		getMembers();
		return props.open;
	}, [props.open]);

	const handleClose = () => {
		props.setOpen(false);
		setErrors({});
		props.setSaveClicked(false);
		props.setContact(null);
	};

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};

		if (!contact?.id)
			localErrors.contactInfo = t("contact.person-required", {
				ns: "validation",
			});

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		event.stopPropagation();

		const isValid = await validateFields();

		if (isValid) {
			props.setSaveClicked(true);
			if (contact?.id) {
				updatePersonContact(contact).then(() => {
					props.setContact?.({ ...contact });
					props.refreshData(contact);
					handleClose();
				});
			} else {
				createPersonContact(contact, props.companyId).then((res: Contact) => {
					const newContact = {
						...setContactValues(contact),
						id: res.id,
						contactUsage: contact?.contactUsage ?? [],
						informationId: contact?.informationId ?? "",
					};

					props.setContact?.(newContact);
					props.refreshData(newContact);
					handleClose();
				});
			}
		}
	};

	const tabs = [
		{
			label: t("tab.contact-information"),
			key: TabKeys.CONTACT_INFORMATION,
			content: (
				<Box sx={{ marginTop: 2 }}>
					<Dropdown
						element={selectedContact}
						setElement={(value) => {
							setSelectedContact(value);
							props.setContact(value?.item as ContactExtended);
						}}
						elements={elements}
						searchLabel={t("member")}
						error={errors.contactInfo as string}
						setError={(value) => setErrors({ ...errors, contactInfo: value })}
						doNotShowMenu={true}
					/>
				</Box>
			),
		},
		{
			label: t("tab.address-information"),
			key: TabKeys.ADDRESS_INFORMATION,
			content: <Address isLoaded={!!contact} contact={contact} setContact={props.setContact} />,
		},
		{
			label: t("tab.department-information"),
			key: TabKeys.DEPARTMENT_INFORMATION,
			content: <DepartmentInfo contact={contact} setContact={props.setContact} />,
		},
		{
			label: t("tab.email-configuration"),
			key: TabKeys.ACTIONS,
			content: <DialogActions contact={contact} setContact={props.setContact} />,
		},
	];

	return (
		<DialogTabs
			open={open}
			handleSubmit={handleSubmit}
			handleClose={handleClose}
			setValue={setTabIndex}
			value={tabIndex}
			title={contact?.id ? t("contact.edit") : t("contact.new")}
			tabs={tabs}
			errors={errors}
			saveClicked={props.saveClicked}
			ruleFactory={props.ruleFactory}
		/>
	);
}
